/** Subir fotos y archivos, y enseñarlos. Lo usan las solicitudes, sus comentarios y los de las
 * páginas.
 *
 * Funciona en dos pasos, como cualquier chat: el archivo **se sube al elegirlo** (así se ve que va
 * bien y se pueden quitar los que sobran) y sus identificadores se mandan junto con el texto al
 * enviar. Los que se suben y luego se descartan los borra el servidor a los dos días.
 */
import { useCallback, useEffect, useRef, useState, type ClipboardEvent, type DragEvent } from 'react'
import { createPortal } from 'react-dom'

import { ErrorDeApi, api } from '../api'
import type { Adjunto } from '../tipos'

const MB = 1024 * 1024
const MAX_ARCHIVOS = 10
const MAX_CLIENTE = 25 * MB

export function tamano(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < MB) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / MB).toFixed(1)} MB`
}

export function iconoDe(tipo: string): string {
  if (tipo === 'application/pdf') return '📕'
  if (tipo === 'application/zip') return '🗜️'
  if (tipo.startsWith('text/')) return '📝'
  if (tipo.startsWith('video/')) return '🎞️'
  return '📄'
}

// ───────────────────────────── Subir ─────────────────────────────

export interface Pendiente {
  clave: number
  nombre: string
  tipo: string
  bytes: number
  /** Una vista previa local mientras sube, para que se vea al momento. */
  vista: string | null
  estado: 'subiendo' | 'listo' | 'error'
  token?: string
  error?: string
}

let contador = 0

/** Los archivos de un evento de pegar o de soltar. Una captura pegada con Ctrl+V llega aquí. */
export function archivosDe(datos: DataTransfer | null): File[] {
  if (!datos) return []
  return [...datos.files].filter((f) => f.size > 0 || f.type)
}

export function useSubidas(rutaArchivos: string, campos: Record<string, string> = {}) {
  const [pendientes, setPendientes] = useState<Pendiente[]>([])
  const camposRef = useRef(campos)
  camposRef.current = campos

  const poner = useCallback((clave: number, cambios: Partial<Pendiente>) => {
    setPendientes((ps) => ps.map((p) => (p.clave === clave ? { ...p, ...cambios } : p)))
  }, [])

  const anadir = useCallback(
    (archivos: File[]) => {
      const nuevos: Pendiente[] = []
      for (const f of archivos) {
        const clave = ++contador
        nuevos.push({
          clave, nombre: f.name || 'captura.png', tipo: f.type, bytes: f.size,
          vista: f.type.startsWith('image/') ? URL.createObjectURL(f) : null,
          estado: f.size > MAX_CLIENTE ? 'error' : 'subiendo',
          error: f.size > MAX_CLIENTE ? 'Pasa de 25 MB.' : undefined,
        })
        if (f.size > MAX_CLIENTE) continue
        const formulario = new FormData()
        // Una captura pegada no trae nombre: se le da uno.
        formulario.append('archivo', f, f.name || 'captura.png')
        for (const [k, v] of Object.entries(camposRef.current)) formulario.append(k, v)
        api
          .subir<{ token: string }>(rutaArchivos, formulario)
          .then((r) => poner(clave, { estado: 'listo', token: r.token }))
          .catch((e) =>
            poner(clave, {
              estado: 'error',
              error: e instanceof ErrorDeApi ? e.message : 'No se ha podido subir.',
            }),
          )
      }
      setPendientes((ps) => [...ps, ...nuevos].slice(0, MAX_ARCHIVOS))
    },
    [rutaArchivos, poner],
  )

  const quitar = useCallback((clave: number) => {
    setPendientes((ps) => {
      const p = ps.find((x) => x.clave === clave)
      if (p?.vista) URL.revokeObjectURL(p.vista)
      return ps.filter((x) => x.clave !== clave)
    })
  }, [])

  const limpiar = useCallback(() => {
    setPendientes((ps) => {
      ps.forEach((p) => p.vista && URL.revokeObjectURL(p.vista))
      return []
    })
  }, [])

  // Las vistas previas locales se sueltan al cerrar.
  useEffect(() => () => pendientes.forEach((p) => p.vista && URL.revokeObjectURL(p.vista)), []) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    pendientes,
    anadir,
    quitar,
    limpiar,
    tokens: pendientes.filter((p) => p.estado === 'listo' && p.token).map((p) => p.token as string),
    subiendo: pendientes.some((p) => p.estado === 'subiendo'),
    hayError: pendientes.some((p) => p.estado === 'error'),
    lleno: pendientes.length >= MAX_ARCHIVOS,
  }
}

export type Subidas = ReturnType<typeof useSubidas>

/** El botón de adjuntar y la fila de archivos que se van a enviar. */
export function BotonDeAdjuntar({ subidas, texto = 'Adjuntar' }: { subidas: Subidas; texto?: string }) {
  const entrada = useRef<HTMLInputElement>(null)
  return (
    <>
      <button type="button" className="boton boton--plano" onClick={() => entrada.current?.click()}
              disabled={subidas.lleno} title="Adjuntar fotos o archivos (también puedes pegar o arrastrar)">
        📎 {texto}
      </button>
      <input
        ref={entrada} type="file" multiple hidden
        accept="image/*,video/mp4,video/webm,application/pdf,application/zip,.docx,.xlsx,.pptx,.txt,.log,.csv,.json,.md,.sql,.lua,.cfg,.ini,.yml,.yaml"
        onChange={(e) => {
          subidas.anadir([...(e.target.files ?? [])])
          e.target.value = ''
        }}
      />
    </>
  )
}

export function PendientesDeSubir({ subidas }: { subidas: Subidas }) {
  if (subidas.pendientes.length === 0) return null
  return (
    <ul className="pendientes" aria-label="Archivos por enviar">
      {subidas.pendientes.map((p) => (
        <li key={p.clave} className={`pendiente pendiente--${p.estado}`}>
          {p.vista ? (
            <img src={p.vista} alt="" className="pendiente__vista" />
          ) : (
            <span className="pendiente__icono" aria-hidden="true">{iconoDe(p.tipo)}</span>
          )}
          <span className="pendiente__texto">
            <span className="pendiente__nombre">{p.nombre}</span>
            <span className="pendiente__estado">
              {p.estado === 'subiendo' && 'Subiendo…'}
              {p.estado === 'listo' && tamano(p.bytes)}
              {p.estado === 'error' && (p.error ?? 'No se ha podido subir.')}
            </span>
          </span>
          <button type="button" className="pendiente__quitar" onClick={() => subidas.quitar(p.clave)}
                  aria-label={`Quitar ${p.nombre}`}>✕</button>
        </li>
      ))}
    </ul>
  )
}

/** Los manejadores para pegar y soltar archivos sobre cualquier caja. */
export function manejadoresDeArchivos(subidas: Subidas) {
  return {
    onPaste: (e: ClipboardEvent) => {
      const archivos = archivosDe(e.clipboardData)
      if (archivos.length > 0) {
        e.preventDefault()
        subidas.anadir(archivos)
      }
    },
    onDragOver: (e: DragEvent) => {
      if ([...e.dataTransfer.types].includes('Files')) e.preventDefault()
    },
    onDrop: (e: DragEvent) => {
      const archivos = archivosDe(e.dataTransfer)
      if (archivos.length > 0) {
        e.preventDefault()
        subidas.anadir(archivos)
      }
    },
  }
}

// ───────────────────────────── Enseñar ─────────────────────────────

/** Las fotos se ven a tamaño de miniatura y se amplían al pulsar; los vídeos se reproducen aquí;
 * lo demás se descarga. */
export function ListaDeAdjuntos({
  adjuntos,
  alQuitar,
}: {
  adjuntos: Adjunto[]
  alQuitar?: (a: Adjunto) => void
}) {
  const [abierta, setAbierta] = useState<number | null>(null)
  if (adjuntos.length === 0) return null
  const imagenes = adjuntos.filter((a) => a.imagen)
  const resto = adjuntos.filter((a) => !a.imagen)

  return (
    <div className="adjuntos">
      {imagenes.length > 0 && (
        <div className="adjuntos__fotos">
          {imagenes.map((a, i) => (
            <figure key={a.token} className="adjuntos__foto">
              <button type="button" onClick={() => setAbierta(i)} aria-label={`Ampliar ${a.nombre}`}>
                <img src={a.url} alt={a.nombre} loading="lazy" />
              </button>
              {alQuitar && (
                <button type="button" className="adjuntos__quitar" onClick={() => alQuitar(a)}
                        aria-label={`Quitar ${a.nombre}`}>✕</button>
              )}
            </figure>
          ))}
        </div>
      )}
      {resto.map((a) =>
        a.video ? (
          <div key={a.token} className="adjuntos__video">
            <video controls preload="metadata" src={a.url} />
            <div className="adjuntos__pie">
              <span>{a.nombre} · {tamano(a.bytes)}</span>
              <a href={a.url} download={a.nombre}>Descargar</a>
              {alQuitar && <button type="button" className="boton boton--plano" onClick={() => alQuitar(a)}>Quitar</button>}
            </div>
          </div>
        ) : (
          <div key={a.token} className="adjuntos__archivo">
            <span aria-hidden="true">{iconoDe(a.tipo)}</span>
            <a href={a.url} download={a.nombre} className="adjuntos__nombre">{a.nombre}</a>
            <span className="adjuntos__tamano">{tamano(a.bytes)}</span>
            {alQuitar && (
              <button type="button" className="arbol-boton arbol-boton--visible"
                      onClick={() => alQuitar(a)} aria-label={`Quitar ${a.nombre}`}>✕</button>
            )}
          </div>
        ),
      )}
      {abierta !== null && (
        <Visor imagenes={imagenes} inicial={abierta} alCerrar={() => setAbierta(null)} />
      )}
    </div>
  )
}

/** La foto a pantalla grande, con flechas y Esc. */
function Visor({ imagenes, inicial, alCerrar }: {
  imagenes: Adjunto[]; inicial: number; alCerrar: () => void
}) {
  const [i, setI] = useState(inicial)
  const actual = imagenes[i]

  useEffect(() => {
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') alCerrar()
      else if (e.key === 'ArrowRight') setI((n) => Math.min(n + 1, imagenes.length - 1))
      else if (e.key === 'ArrowLeft') setI((n) => Math.max(n - 1, 0))
    }
    window.addEventListener('keydown', tecla)
    return () => window.removeEventListener('keydown', tecla)
  }, [alCerrar, imagenes.length])

  if (!actual) return null
  return createPortal(
    <div className="visor" role="dialog" aria-modal="true" aria-label={actual.nombre}
         onMouseDown={(e) => e.target === e.currentTarget && alCerrar()}>
      <button className="visor__cerrar" onClick={alCerrar} aria-label="Cerrar">✕</button>
      {i > 0 && <button className="visor__flecha visor__flecha--izq" onClick={() => setI(i - 1)} aria-label="Anterior">‹</button>}
      <img src={actual.url} alt={actual.nombre} />
      {i < imagenes.length - 1 && <button className="visor__flecha visor__flecha--der" onClick={() => setI(i + 1)} aria-label="Siguiente">›</button>}
      <div className="visor__pie">
        {actual.nombre} · {tamano(actual.bytes)}
        {imagenes.length > 1 && ` · ${i + 1} de ${imagenes.length}`}
        {' · '}<a href={actual.url} target="_blank" rel="noopener noreferrer">Abrir original</a>
      </div>
    </div>,
    document.body,
  )
}
